import CloseIcon from '../svg/common/close';
import { CSSProperties, ReactNode, useCallback, useEffect, useRef, MouseEvent } from 'react';

/*
Usage:
-------
<Popup width="400px" height="270px" close={ () => {
    dispatch(
        rootActions.main({
            showLoginPage: false
        })
    );
}}>
    <div className="flex flex-col justify-center items-center w-full h-full my-4 text-center">
        <p className="mb-4">Please log in to your Waves account to hide products you own.</p>
        <a className="w-btn" href="/login?returnurl=%2Fspecials" title="LOG IN NOW" style={{ width: '170px' }}>LOG IN NOW</a>
    </div>
</Popup>
 */

interface IPopupProps {
    width?: string,
    height?: string,
    close?: Function,
    canBeClosed?: boolean,
    children?: ReactNode,
    classes?: string,
    bgClass?: string,
    styles?: CSSProperties,
    iconWidth?: string,
    iconColor?: string,
    btnTop?: string;
    btnRight?: string;
    removeBackdrop?: boolean;
    prevent?: boolean;
}

/**
 * Popup component.
 */
export const Popup = (props: IPopupProps) => {

    const popupRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {

        setTimeout(() => {
            if(closeButtonRef.current) {
                closeButtonRef.current.focus();
            }
            else{
                popupRef.current?.focus();
            }
        }, 10);
    }, []);

    /**
     * close popup handler
     */
    const closePopup = useCallback(() => {
        if (typeof props.close !== 'function') return;
        props.close();
    }, [props])

    /**
     * handle escape key
     */
    const handleEscape = useCallback(async (evt: any) => {
        if (evt.key === 'Escape') {
            closePopup();
        }
    }, [closePopup]);

    useEffect(() => {
        document.addEventListener('keydown', handleEscape);
        document.addEventListener('close-popup', closePopup);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('close-popup', closePopup);
        }
    }, [closePopup, handleEscape]);

    const canBeClosed = props.canBeClosed !== undefined ? props.canBeClosed : true;
    const iconWidth = props.iconWidth !== undefined ? props.iconWidth : '16';

    const handlePopupClick = (evt: MouseEvent<HTMLDivElement>) => {

        if(props.prevent) {
            evt.preventDefault()
            evt.stopPropagation();
        }

        if (!canBeClosed) return;
        closePopup();
    };

    return (
        <div
            ref={ popupRef }
            tabIndex={ 0 }
            className="popup w-screen h-screen fixed left-0 top-0 flex items-center justify-center"
            style={{
                background: props.removeBackdrop ? '' : 'rgba(0,0,0,0.7)',
                zIndex: 100000,
            }}
            onClick={ handlePopupClick }>
            <div
                className={`popup__content flex flex-col rounded ${props.bgClass ?? 'bg-white'} ${props.removeBackdrop ? '' : 'px-4'} overflow-y-auto max-w-full max-h-screen relative ${props.classes}`}
                onClick={evt => {
                    evt.stopPropagation();
                    if(props.prevent) {
                        evt.preventDefault();
                    }
                }}
                style={{
                    ...(props.styles || {}),
                    width: props.width,
                    height: props.height,
                }}
            >
                {
                    canBeClosed &&
                    <button
                        ref={ closeButtonRef }
                        type="button"
                        title="Close Popup"
                        className="absolute"
                        style={{
                            right: props.btnRight ? props.btnRight : '1rem',
                            top: props.btnTop ? props.btnTop : '1rem',
                            zIndex: '99',
                        }}
                        onClick={closePopup}>
                        <CloseIcon
                            width={iconWidth}
                            color={props.iconColor || '#828282'}
                        />
                    </button>
                }

                {props.children}
            </div>
        </div>
    )
};

/*
    max-height: 100vh;
 */
